<script lang="ts">
import type { HTMLAttributes } from 'vue'
import { cn } from '../../../lib/utils'
import type { GatIconProps } from '../../gat-icon'

export interface DataTableCellPopoverTriggerLine {
  text: string
  icon?: GatIconProps['icon']
  class?: HTMLAttributes['class']
}

export interface DataTableCellPopoverProps {
  open?: boolean
  trigger?: {
    line1: DataTableCellPopoverTriggerLine
    line2?: DataTableCellPopoverTriggerLine
    options?: DataTableCellPopoverTriggerProps
  }

  content: {
    header: DataTableCellPopoverContentHeaderProps
  }
  actions?: DataTableCellPopoverActionsProps['actions']
}
</script>

<script setup lang="ts">
import { GatIcon } from '../../gat-icon'
import DataTableCellPopoverActions, {
  type DataTableCellPopoverActionsProps
} from './DataTableCellPopoverActions.vue'
import DataTableCellPopoverContent from './DataTableCellPopoverContent.vue'
import DataTableCellPopoverContentHeader, {
  type DataTableCellPopoverContentHeaderProps
} from './DataTableCellPopoverContentHeader.vue'
import DataTableCellPopoverRoot from './DataTableCellPopoverRoot.vue'
import DataTableCellPopoverTrigger, {
  type DataTableCellPopoverTriggerProps
} from './DataTableCellPopoverTrigger.vue'

const props = defineProps<DataTableCellPopoverProps>()

// eslint-disable-next-line vue/no-dupe-keys
const open = defineModel<boolean>('open')
</script>
<template>
  <DataTableCellPopoverRoot v-model:open="open">
    <DataTableCellPopoverTrigger v-bind="props.trigger?.options">
      <slot name="trigger">
        <div v-if="props.trigger" class="flex flex-col gap-1 overflow-hidden leading-none">
          <div :class="cn('flex items-center gap-1', props.trigger.line1.class)">
            <div v-if="props.trigger.line1.icon">
              <gat-icon
                :icon="props.trigger.line1.icon"
                :class="cn('size-3.5 shrink-0', props.trigger.line1.class)"
                :stroke-width="1.5"
              />
            </div>
            <div class="truncate leading-none">{{ props.trigger.line1.text }}</div>
          </div>
          <div
            v-if="props.trigger.line2"
            :class="cn('flex items-center gap-1', props.trigger.line2.class)"
          >
            <div v-if="props.trigger.line2.icon">
              <gat-icon
                :icon="props.trigger.line2.icon"
                class="size-3.5 shrink-0"
                :stroke-width="1.5"
              />
            </div>
            <div class="truncate leading-none">{{ props.trigger.line2.text }}</div>
          </div>
        </div>
        <div v-else>No trigger.</div>
      </slot>
    </DataTableCellPopoverTrigger>
    <DataTableCellPopoverContent>
      <DataTableCellPopoverContentHeader v-bind="props.content.header" />
      <slot />
      <DataTableCellPopoverActions :actions="props.actions" />
    </DataTableCellPopoverContent>
  </DataTableCellPopoverRoot>
</template>
